// The geometry of one value against two benchmark marks on a bar (Jayson's
// 10/21 note on the engagement bar, spec D5): ONE coordinate system for the
// track, its fill and dot, the marks' ticks and every label, so the dot can
// never read as below a mark it is above. Pure, no React: the profile's
// engagement bar (desktop EngagementBenchmark, phone EngagementCard) and the
// coach bars draw from it.
//
//   benchmarkScale({ value, low, high, edge = 8 }) -> {
//     mode         "linear" | "broken"
//     end          the axis end in the value's units: E on a linear axis, the
//                  end of the linear part (2 x high) on a broken one
//     pos(x)       a position 0 to 100 on this axis for any x (past the
//                  linear part of a broken axis: the value's mark)
//     value        the value, null when missing (no dot, no fill, no label)
//     valuePos     where the dot and the end of the fill sit, null without a
//                  value
//     valueAnchor  how the value's label sits on its dot: "center", "start"
//                  (left-aligned on the dot, under `edge`%) or "end"
//                  (right-aligned on the dot, past 100 - `edge`%)
//     lowPos, highPos  the marks' ticks, null for a missing mark
//     breakPos     where a broken axis draws its break, null on a linear one
//     offScale     true on a broken axis: the value's real number prints
//   }
//
// The axis (UI 2.4):
//  - Linear from 0 while the value is at most 3 x high, ending at
//    E = ceil(max(1.6 x high, 1.15 x value)): the high mark never sits right
//    of 62.5%, so its label always fits, and the value keeps some room.
//  - Past 3 x high, a broken axis: 0 to 2 x high fills 0 to 80% of the
//    track, the break sits at 87% and the dot at 94% with its real number,
//    so an outlier reads as far above the top mark without crushing the
//    marks into the left end.
//  - Without a high mark the low one sets the axis; without either, the
//    value alone (an axis of at least 1).
// The labels never collide because they sit in separate rows: the value's
// above the track, anchored as `valueAnchor` says; the marks' below it, the
// low mark's right-aligned to its tick and growing left (clamped to the
// track's start), the high mark's left-aligned to its tick and growing
// right, so the two grow away from each other.

export const LINEAR_SHARE = 80;
export const BREAK_POS = 87;
export const OFF_SCALE_POS = 94;

const numberOr = (v) => {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
  const n = typeof v === "string" ? parseFloat(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
};

const clampPct = (v) => Math.max(0, Math.min(100, v));

// The next whole number up, without a float's 8.000000000000002 reading as 9.
export const ceilToWhole = (x) => Math.ceil(Number(Number(x).toFixed(9)));

// How a label sits on a mark at `pos` (0 to 100): centred on it, or lined up
// with it near an end so it never runs off the track.
export const edgeAnchor = (pos, edge = 8) => {
  if (typeof pos !== "number" || !Number.isFinite(pos)) return "center";
  if (pos < edge) return "start";
  if (pos > 100 - edge) return "end";
  return "center";
};

export const benchmarkScale = ({ value, low, high, edge = 8 } = {}) => {
  const v = numberOr(value);
  const lo = numberOr(low);
  const hi = numberOr(high);
  const shown = v == null ? null : Math.max(0, v);
  const top = hi != null && hi > 0 ? hi : null;
  const broken = shown != null && top != null && shown > 3 * top;

  let end;
  let pos;
  if (broken) {
    end = 2 * top;
    pos = (x) => {
      const n = numberOr(x);
      if (n == null) return null;
      return n > end ? OFF_SCALE_POS : (LINEAR_SHARE * Math.max(0, n)) / end;
    };
  } else {
    const base = top ?? (lo != null && lo > 0 ? lo : null);
    end = Math.max(1, ceilToWhole(Math.max(base != null ? 1.6 * base : 0, shown != null ? 1.15 * shown : 0)));
    pos = (x) => {
      const n = numberOr(x);
      return n == null ? null : clampPct((100 * Math.max(0, Math.min(n, end))) / end);
    };
  }

  const valuePos = shown == null ? null : broken ? OFF_SCALE_POS : pos(shown);
  return {
    mode: broken ? "broken" : "linear",
    end,
    pos,
    value: v,
    valuePos,
    valueAnchor: edgeAnchor(valuePos, edge),
    lowPos: lo == null ? null : pos(lo),
    highPos: hi == null ? null : pos(hi),
    breakPos: broken ? BREAK_POS : null,
    offScale: broken,
  };
};

// Where the value stands against the marks, in words, for the bar's
// accessible name: "Engagement rate 5.3%, above the top 10% mark of 5%;
// position average 1.8%". `format` prints a number of the scale ("5.3%"),
// `formatValue` the value itself when it prints otherwise than the marks
// (its label's "5.0%"; `format` by default), `name` the value's name,
// `lowName` / `highName` the marks'. The value is compared as given: a
// caller that prints it rounded passes it rounded, so the words never say
// "1.8%, below the low mark of 1.8%".
export const benchmarkSummary = ({
  value,
  low,
  high,
  name = "Value",
  lowName = "low mark",
  highName = "high mark",
  format = String,
  formatValue = format,
} = {}) => {
  const v = numberOr(value);
  const lo = numberOr(low);
  const hi = numberOr(high);
  const marks = [lo != null && `${lowName} ${format(lo)}`, hi != null && `${highName} ${format(hi)}`].filter(Boolean);
  if (v == null) return [`${name} not measured yet`, marks.join(", ")].filter(Boolean).join("; ");
  const at = (x) => Math.abs(v - x) < 1e-9;
  let where = "";
  let rest = [];
  if (hi != null && (v > hi || at(hi))) {
    where = `${at(hi) ? "at" : "above"} the ${highName} of ${format(hi)}`;
    rest = lo != null ? [`${lowName} ${format(lo)}`] : [];
  } else if (lo != null && (v < lo || at(lo))) {
    where = `${at(lo) ? "at" : "below"} the ${lowName} of ${format(lo)}`;
    rest = hi != null ? [`${highName} ${format(hi)}`] : [];
  } else if (lo != null && hi != null) {
    where = `between the ${lowName} of ${format(lo)} and the ${highName} of ${format(hi)}`;
  } else if (hi != null) {
    where = `below the ${highName} of ${format(hi)}`;
  } else if (lo != null) {
    where = `above the ${lowName} of ${format(lo)}`;
  }
  const head = [`${name} ${formatValue(v)}`, where].filter(Boolean).join(", ");
  return rest.length ? `${head}; ${rest.join(", ")}` : head;
};

export default benchmarkScale;
